/**
 * Page Fiche Salarié - Phase 1 + Phase 2
 * Onglets: Infos personnelles, Infos professionnelles, Documents, Contrats, Fiches de paie, Médical, Absences
 */

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { 
  ArrowLeft, User, Briefcase, FileText, FileSignature, Receipt,
  Edit2, Save, X, Eye, EyeOff, AlertCircle, RefreshCw, Stethoscope, CalendarDays,
  GraduationCap, MessageSquare, Download, Loader2, UserMinus, UserCheck, Trash2, Wallet
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import PersonalInfoTab from "../../components/rh/tabs/PersonalInfoTab";
import ProfessionalInfoTab from "../../components/rh/tabs/ProfessionalInfoTab";
import DocumentsTab from "../../components/rh/tabs/DocumentsTab";
import ContractsTab from "../../components/rh/tabs/ContractsTab";
import PayslipsTab from "../../components/rh/tabs/PayslipsTab";
import MedicalTab from "../../components/rh/tabs/MedicalTab";
import AbsencesTab from "../../components/rh/tabs/AbsencesTab";
import TrainingsTab from "../../components/rh/tabs/TrainingsTab";
import InterviewsTab from "../../components/rh/tabs/InterviewsTab";
import AdvancePaymentTab from "../../components/rh/tabs/AdvancePaymentTab";
import { DatePickerFr } from "../../components/ui/date-picker-fr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badges
const STATUS_BADGES = {
  active: { label: "Actif", className: "bg-emerald-100 text-emerald-700" },
  on_leave: { label: "En congé", className: "bg-amber-100 text-amber-700" },
  terminated: { label: "Sorti", className: "bg-slate-100 text-slate-500" },
};

// Contract type labels
const CONTRACT_LABELS = {
  cdi: "CDI",
  cdd: "CDD",
  alternance: "Alternance",
  stage: "Stage",
  interim: "Intérim",
};

// Termination reasons
const TERMINATION_REASONS = {
  resignation: "Démission",
  dismissal: "Licenciement",
  mutual_agreement: "Rupture conventionnelle",
  end_of_contract: "Fin de CDD",
  retirement: "Départ en retraite",
  probation_end: "Fin de période d'essai",
  other: "Autre",
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'Europe/Paris'
    });
  } catch {
    return dateStr;
  }
};

export default function SalarieDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  // State
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState({ documents: 0, contracts: 0, payslips: 0 });
  const [exportingZip, setExportingZip] = useState(false);
  
  // Departure dialog state
  const [showDepartureDialog, setShowDepartureDialog] = useState(false);
  const [departureDate, setDepartureDate] = useState("");
  const [departureReason, setDepartureReason] = useState("");
  const [savingDeparture, setSavingDeparture] = useState(false);
  
  // Reactivation dialog state  
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [savingReactivation, setSavingReactivation] = useState(false);
  
  // Delete dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Current tab
  const currentTab = searchParams.get("tab") || "personal";
  
  // Fetch employee data
  const fetchEmployee = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [empRes, docsRes, contractsRes, payslipsRes] = await Promise.all([
        axios.get(`${API}/hr/employees/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/hr/employees/${id}/documents`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/hr/employees/${id}/contracts`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/hr/employees/${id}/payslips/years`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setEmployee(empRes.data);
      setCounts({
        documents: docsRes.data.total || 0,
        contracts: contractsRes.data.total || 0,
        payslips: payslipsRes.data.years?.reduce((acc, y) => acc + y.count, 0) || 0,
      });
      
    } catch (err) {
      console.error("Error fetching employee:", err);
      if (err.response?.status === 404) {
        setError("Salarié non trouvé");
      } else {
        setError("Impossible de charger les données du salarié");
      }
    } finally {
      setLoading(false);
    }
  }, [id, token]);
  
  useEffect(() => {
    fetchEmployee();
  }, [fetchEmployee]);
  
  // Handle tab change
  const handleTabChange = (value) => {
    setSearchParams({ tab: value }, { replace: true });
  };
  
  // Handle employee update
  const handleEmployeeUpdate = (updatedEmployee) => {
    setEmployee(updatedEmployee);
    toast.success("Modifications enregistrées");
  };
  
  // Export employee ZIP
  const handleExportZip = async () => {
    setExportingZip(true);
    try {
      const response = await axios.get(
        `${API}/hr/export/employees/${id}/zip`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dossier_salarie_${id}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Dossier exporté avec succès");
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Erreur lors de l'export");
    } finally {
      setExportingZip(false);
    }
  };
  
  // Handle departure (terminate employee)
  const handleDeparture = async () => {
    if (!departureDate || !departureReason) {
      toast.error("Veuillez renseigner la date et le motif de départ");
      return;
    }
    
    setSavingDeparture(true);
    try {
      await axios.put(
        `${API}/hr/employees/${id}`,
        {
          status: "terminated",
          professional_info: {
            ...employee.professional_info,
            termination_date: departureDate,
            termination_reason: departureReason,
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh employee data
      await fetchEmployee();
      setShowDepartureDialog(false);
      setDepartureDate("");
      setDepartureReason("");
      toast.success("Départ enregistré avec succès");
    } catch (err) {
      console.error("Error saving departure:", err);
      toast.error("Erreur lors de l'enregistrement du départ");
    } finally {
      setSavingDeparture(false);
    }
  };
  
  // Handle reactivation (set employee back to active)
  const handleReactivation = async () => {
    setSavingReactivation(true);
    try {
      await axios.put(
        `${API}/hr/employees/${id}`,
        {
          status: "active",
          professional_info: {
            ...employee.professional_info,
            termination_date: null,
            termination_reason: null,
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh employee data
      await fetchEmployee();
      setShowReactivateDialog(false);
      toast.success("Salarié réactivé avec succès");
    } catch (err) {
      console.error("Error reactivating:", err);
      toast.error("Erreur lors de la réactivation");
    } finally {
      setSavingReactivation(false);
    }
  };
  
  // Handle permanent deletion
  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await axios.delete(
        `${API}/hr/employees/${id}/permanent`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || "Salarié supprimé définitivement");
      navigate("/rh/salaries");
    } catch (err) {
      console.error("Error deleting employee:", err);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="p-4 md:p-6 space-y-6" data-testid="salarie-detail-loading">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="p-4 md:p-6" data-testid="salarie-detail-error">
        <Card className="border-slate-200">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">{error}</h3>
            <div className="flex items-center justify-center gap-3 mt-6">
              <Button variant="outline" onClick={() => navigate("/rh/salaries")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à la liste
              </Button>
              <Button onClick={fetchEmployee}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Réessayer
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (!employee) return null;
  
  const personal = employee.personal_info || {};
  const professional = employee.professional_info || {};
  const statusInfo = STATUS_BADGES[employee.status] || STATUS_BADGES.active;
  const contractLabel = CONTRACT_LABELS[professional.contract_type] || professional.contract_type;

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6" data-testid="salarie-detail-page">
      {/* Header - Mobile */}
      <div className="sm:hidden">
        <div className="flex items-center gap-3 mb-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/rh/salaries")}
            className="shrink-0"
            data-testid="back-btn-mobile"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-bold text-slate-900 truncate">
            {personal.first_name} {personal.last_name}
          </h1>
        </div>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-base shrink-0">
            {personal.first_name?.charAt(0)}{personal.last_name?.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm text-slate-600">{employee.matricule}</span>
              <Badge className={statusInfo.className + " text-[10px]"}>{statusInfo.label}</Badge>
            </div>
            <p className="text-sm text-slate-600 truncate">{professional.job_title || "Poste non défini"}</p>
            <p className="text-xs text-slate-400">{contractLabel}</p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button
            variant="outline"
            onClick={handleExportZip}
            disabled={exportingZip}
            data-testid="export-zip-btn-mobile"
            className="w-full"
            size="sm"
          >
            {exportingZip ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Exporter le dossier
          </Button>
          {employee.status === "terminated" ? (
            <Button
              variant="outline"
              onClick={() => setShowReactivateDialog(true)}
              className="w-full text-emerald-600 border-emerald-200 hover:bg-emerald-50"
              size="sm"
              data-testid="reactivate-btn-mobile"
            >
              <UserCheck className="w-4 h-4 mr-2" />
              Réactiver le salarié
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => setShowDepartureDialog(true)}
              className="w-full text-red-600 border-red-200 hover:bg-red-50"
              size="sm"
              data-testid="departure-btn-mobile"
            >
              <UserMinus className="w-4 h-4 mr-2" />
              Enregistrer le départ
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setShowDeleteDialog(true)}
            className="w-full text-red-600 border-red-200 hover:bg-red-50"
            size="sm"
            data-testid="delete-btn-mobile"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer définitivement
          </Button>
        </div>
      </div>

      {/* Header - Desktop */}
      <div className="hidden sm:flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex items-start gap-4">
          {/* Back button */}
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/rh/salaries")}
            className="shrink-0 mt-1"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          
          {/* Avatar + Info */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-xl shrink-0">
              {personal.first_name?.charAt(0)}{personal.last_name?.charAt(0)}
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900">
                  {personal.first_name} {personal.last_name}
                </h1>
                <Badge className={statusInfo.className}>{statusInfo.label}</Badge>
              </div>
              <p className="text-slate-500 mt-1">
                <span className="font-mono">{employee.matricule}</span>
                <span className="mx-2">·</span>
                <span>{professional.job_title || "Poste non défini"}</span>
                <span className="mx-2">·</span>
                <span>{contractLabel}</span>
              </p>
              {professional.hire_date && (
                <p className="text-sm text-slate-400 mt-0.5">
                  Embauché{personal.first_name?.endsWith('e') ? 'e' : ''} le {formatDate(professional.hire_date)}
                </p>
              )}
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            onClick={handleExportZip}
            disabled={exportingZip}
            data-testid="export-zip-btn"
          >
            {exportingZip ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Exporter
          </Button>
          {employee.status === "terminated" ? (
            <Button
              variant="outline"
              onClick={() => setShowReactivateDialog(true)}
              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
              data-testid="reactivate-btn"
            >
              <UserCheck className="w-4 h-4 mr-2" />
              Réactiver
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => setShowDepartureDialog(true)}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="departure-btn"
            >
              <UserMinus className="w-4 h-4 mr-2" />
              Départ
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setShowDeleteDialog(true)}
            className="text-red-600 border-red-200 hover:bg-red-50"
            data-testid="delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer
          </Button>
        </div>
      </div>
      
      {/* Tabs - Scroll horizontal sur mobile */}
      <Tabs value={currentTab} onValueChange={handleTabChange} className="space-y-4 md:space-y-6">
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="bg-slate-100 p-1 inline-flex min-w-max sm:flex-wrap">
            <TabsTrigger value="personal" className="data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-personal">
              <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Infos personnelles</span>
              <span className="sm:hidden">Perso</span>
            </TabsTrigger>
            <TabsTrigger value="professional" className="data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-professional">
              <Briefcase className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Infos professionnelles</span>
              <span className="sm:hidden">Pro</span>
            </TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-documents">
              <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Documents</span>
              <span className="sm:hidden">Docs</span>
              <span className="ml-1">({counts.documents})</span>
            </TabsTrigger>
            <TabsTrigger value="contracts" className="data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-contracts">
              <FileSignature className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Contrats</span>
              <span className="sm:hidden">Ctr</span>
              <span className="ml-1">({counts.contracts})</span>
            </TabsTrigger>
            <TabsTrigger value="payslips" className="data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-payslips">
              <Receipt className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Fiches de paie</span>
              <span className="sm:hidden">Paie</span>
              <span className="ml-1">({counts.payslips})</span>
            </TabsTrigger>
            <TabsTrigger value="medical" className="data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-medical">
              <Stethoscope className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Médical</span>
              <span className="sm:hidden">Méd</span>
            </TabsTrigger>
            <TabsTrigger value="absences" className="data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-absences">
              <CalendarDays className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Absences</span>
              <span className="sm:hidden">Abs</span>
            </TabsTrigger>
            <TabsTrigger value="trainings" className="data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-trainings">
              <GraduationCap className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Formations</span>
              <span className="sm:hidden">Form</span>
            </TabsTrigger>
            <TabsTrigger value="interviews" className="data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-interviews">
              <MessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Entretiens</span>
              <span className="sm:hidden">Entr</span>
            </TabsTrigger>
            <TabsTrigger value="advance" className="data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-advance">
              <Wallet className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Acompte</span>
              <span className="sm:hidden">Acp</span>
            </TabsTrigger>
          </TabsList>
        </div>
        
        {/* Tab Contents */}
        <TabsContent value="personal">
          <PersonalInfoTab 
            employee={employee} 
            onUpdate={handleEmployeeUpdate}
          />
        </TabsContent>
        
        <TabsContent value="professional">
          <ProfessionalInfoTab 
            employee={employee} 
            onUpdate={handleEmployeeUpdate}
          />
        </TabsContent>
        
        <TabsContent value="documents">
          <DocumentsTab 
            employee={employee} 
            onUpdate={fetchEmployee}
          />
        </TabsContent>
        
        <TabsContent value="contracts">
          <ContractsTab 
            employee={employee} 
            onUpdate={fetchEmployee}
          />
        </TabsContent>
        
        <TabsContent value="payslips">
          <PayslipsTab 
            employee={employee} 
            onUpdate={fetchEmployee}
          />
        </TabsContent>
        
        <TabsContent value="medical">
          <MedicalTab 
            employee={employee}
          />
        </TabsContent>
        
        <TabsContent value="absences">
          <AbsencesTab 
            employee={employee}
          />
        </TabsContent>
        
        <TabsContent value="trainings">
          <TrainingsTab 
            employee={employee}
          />
        </TabsContent>
        
        <TabsContent value="interviews">
          <InterviewsTab 
            employee={employee}
          />
        </TabsContent>
        
        <TabsContent value="advance">
          <AdvancePaymentTab 
            employeeId={employee?.id}
            token={localStorage.getItem('token')}
            onUpdate={fetchEmployee}
          />
        </TabsContent>
      </Tabs>
      
      {/* Departure Dialog */}
      <Dialog open={showDepartureDialog} onOpenChange={setShowDepartureDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <UserMinus className="w-5 h-5" />
              Enregistrer le départ
            </DialogTitle>
            <DialogDescription>
              Renseignez la date de départ effective et le motif pour {personal.first_name} {personal.last_name}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="departure-date">Date de départ *</Label>
              <DatePickerFr
                value={departureDate}
                onChange={setDepartureDate}
                placeholder="Sélectionner la date"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="departure-reason">Motif de départ *</Label>
              <Select value={departureReason} onValueChange={setDepartureReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner le motif" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TERMINATION_REASONS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowDepartureDialog(false)}
              disabled={savingDeparture}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleDeparture}
              disabled={savingDeparture || !departureDate || !departureReason}
              className="bg-red-600 hover:bg-red-700"
            >
              {savingDeparture ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <UserMinus className="w-4 h-4 mr-2" />
                  Confirmer le départ
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reactivation Dialog */}
      <Dialog open={showReactivateDialog} onOpenChange={setShowReactivateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600">
              <UserCheck className="w-5 h-5" />
              Réactiver le salarié
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir réactiver {personal.first_name} {personal.last_name} ? 
              La date et le motif de départ seront effacés.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowReactivateDialog(false)}
              disabled={savingReactivation}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleReactivation}
              disabled={savingReactivation}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingReactivation ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Réactivation...
                </>
              ) : (
                <>
                  <UserCheck className="w-4 h-4 mr-2" />
                  Confirmer la réactivation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Supprimer définitivement ce salarié ?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Vous êtes sur le point de supprimer <strong>{personal.first_name} {personal.last_name}</strong> ({employee?.matricule}).
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 font-medium text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Cette action est irréversible !
                </p>
                <p className="text-red-700 text-sm mt-1">
                  Toutes les données associées seront supprimées :
                </p>
                <ul className="text-red-600 text-sm mt-1 list-disc list-inside">
                  <li>Documents ({counts.documents})</li>
                  <li>Contrats ({counts.contracts})</li>
                  <li>Fiches de paie ({counts.payslips})</li>
                  <li>Visites médicales, absences, formations, entretiens</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer définitivement
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
